import React from 'react';
import { Link } from 'react-router-dom';
import ProgressBar from './ProgressBar';

const FundraiserListPage = ({ fundraisers }) => {
  const approvedFundraisers = fundraisers.filter(f => f.approved);
  const individualFundraisers = approvedFundraisers.filter(f => f.type === 'individual');
  const projectFundraisers = approvedFundraisers.filter(f => f.type === 'project');

  const renderFundraiserCard = (fundraiser) => (
    <div key={fundraiser.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
      {/* Circular Image at Top */}
      <div className="p-6 pb-0 flex justify-center">
        <img
          src={fundraiser.photo}
          alt={fundraiser.name}
          className="w-32 h-32 rounded-full object-cover border-4 border-gray-100 shadow-md"
        />
      </div>

      <div className="p-6 pt-4">
        <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">{fundraiser.name}</h3>
        {fundraiser.type === 'project' && (
          <div className="flex justify-center mb-2">
            <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
              Team Project
            </span>
          </div>
        )}
        <p className="text-gray-600 mb-4 line-clamp-3 text-center text-sm">{fundraiser.bio}</p>

        <div className="mb-6">
          <ProgressBar
            goal={fundraiser.goal}
            raised={fundraiser.raised}
            showLegend={false}
          />
        </div>

        <Link
          to={`/fundraiser/${fundraiser.id}`}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-center block"
        >
          View Details & Donate
        </Link>
      </div>
    </div>
  );

  // Calculate totals by category
  const projectTotal = projectFundraisers.reduce((sum, f) => sum + (f.raised || 0), 0);
  const projectGoal = projectFundraisers.reduce((sum, f) => sum + f.goal, 0);

  const individualTotal = individualFundraisers.reduce((sum, f) => sum + (f.raised || 0), 0);
  const individualGoal = individualFundraisers.reduce((sum, f) => sum + f.goal, 0);

  // Calculate overfunding from individuals (amounts raised above their goal)
  const overfundingDetails = individualFundraisers
    .filter(f => (f.raised || 0) > f.goal)
    .map(f => ({
      name: f.name,
      excess: (f.raised || 0) - f.goal,
      raised: f.raised || 0,
      goal: f.goal
    }));

  const totalOverfunding = overfundingDetails.reduce((sum, d) => sum + d.excess, 0);

  // Total team raised includes everything
  const totalRaised = projectTotal + individualTotal;
  const totalGoal = projectGoal + individualGoal;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Costa Rica Missions 2025-2026</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
          Join us in supporting our mission team as they serve communities in Costa Rica.
          Each team member is raising funds to cover their trip costs and contribute to local projects.
        </p>

        {/* Total Team Fundraising Display */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 max-w-4xl mx-auto shadow-lg border border-green-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">🎯 Team Fundraising Progress</h2>

          {/* Overall Total */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-sm text-gray-600 mb-1">Total Raised</p>
              <p className="text-3xl font-bold text-green-600">${totalRaised.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-sm text-gray-600 mb-1">Team Goal</p>
              <p className="text-3xl font-bold text-blue-600">${totalGoal.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-sm text-gray-600 mb-1">Progress</p>
              <p className="text-3xl font-bold text-purple-600">
                {totalGoal > 0 ? Math.round((totalRaised / totalGoal) * 100) : 0}%
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6 bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-500"
              style={{ width: `${totalGoal > 0 ? Math.min((totalRaised / totalGoal) * 100, 100) : 0}%` }}
            />
          </div>

          {/* Category Breakdown */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Category Breakdown</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {/* Team Projects */}
              {projectFundraisers.length > 0 && (
                <div className="border-l-4 border-purple-500 pl-4 min-w-[250px]">
                  <p className="text-sm font-medium text-gray-600 mb-1">Team Projects</p>
                  <p className="text-2xl font-bold text-purple-600">
                    ${projectTotal.toLocaleString()} <span className="text-lg text-gray-500">/ ${projectGoal.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {projectGoal > 0 ? Math.round((projectTotal / projectGoal) * 100) : 0}% funded
                  </p>
                </div>
              )}

              {/* Individuals */}
              {individualFundraisers.length > 0 && (
                <div className="border-l-4 border-blue-500 pl-4 min-w-[250px]">
                  <p className="text-sm font-medium text-gray-600 mb-1">Individual Fundraisers</p>
                  <p className="text-2xl font-bold text-blue-600">
                    ${individualTotal.toLocaleString()} <span className="text-lg text-gray-500">/ ${individualGoal.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {individualGoal > 0 ? Math.round((individualTotal / individualGoal) * 100) : 0}% funded
                  </p>
                </div>
              )}
            </div>

            {/* Overfunding Attribution */}
            {overfundingDetails.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center mb-3">
                  <span className="text-lg">🌟</span>
                  <h4 className="text-md font-semibold text-gray-800 ml-2">Bonus Contributions to Team</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  These team members exceeded their individual goals! Their extra contributions go toward team needs:
                </p>
                <div className="space-y-2">
                  {overfundingDetails.map((detail, index) => (
                    <div key={index} className="flex items-center justify-between bg-yellow-50 rounded-lg px-4 py-2 border border-yellow-200">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-800">{detail.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          (${detail.raised.toLocaleString()} / ${detail.goal.toLocaleString()})
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-yellow-700">+${detail.excess.toLocaleString()}</span>
                        <span className="text-xs text-gray-500 ml-1">to team</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right">
                  <span className="text-sm font-semibold text-gray-700">Total Bonus: </span>
                  <span className="text-lg font-bold text-yellow-600">${totalOverfunding.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {approvedFundraisers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🙏</div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">No fundraisers yet</h2>
          <p className="text-gray-500 mb-6">Be the first to join our mission team!</p>
          <Link
            to="/signup"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Join the Team
          </Link>
        </div>
      ) : (
        <>
          {/* Team Projects Section */}
          {projectFundraisers.length > 0 && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Team Projects</h2>
                <p className="text-gray-600">
                  Support our collective efforts and shared expenses for the missions trip
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projectFundraisers.map(renderFundraiserCard)}
              </div>
            </div>
          )}

          {/* Individual Team Members Section */}
          {individualFundraisers.length > 0 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Our Team Members</h2>
                <p className="text-gray-600">
                  Meet the individuals who are dedicating their time and hearts to serve in Costa Rica
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {individualFundraisers.map(renderFundraiserCard)}
              </div>
            </div>
          )}
        </>
      )}

      <div className="text-center mt-16">
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Want to Join Our Team?</h2>
          <p className="text-gray-600 mb-6">We're still accepting applications for our Costa Rica missions trip.</p>
          <Link
            to="/signup"
            className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors inline-block"
          >
            Apply to Join
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FundraiserListPage;